import React from 'react';
import '../styles/ProductTierCard.css';

interface ProductTier {
  id: string;
  name: string;
  description: string;
  price_id: string;
  amount: number;
  currency: string;
  interval: string;
  features: string[] | null;
  metadata: Record<string, string>;
}

interface UserSubscription {
  subscription_id: string;
  stripe_product_id: string;
  stripe_price_id: string;
  status: string;
  current_period_end: string;
}

interface ProductTierCardProps {
  tier: ProductTier;
  isCurrentPlan: boolean;
  currentSubscription: UserSubscription | null;
  hasSubscription: boolean;
  allTiers: ProductTier[];
  onSelect: () => void;
  onUpgrade?: () => void;
  onDowngrade?: () => void;
  onCancel?: () => void;
  addDebugLog?: (message: string) => void;
  displayOnly?: boolean; // New prop for home page display
}

const ProductTierCard: React.FC<ProductTierCardProps> = ({
  tier,
  isCurrentPlan,
  currentSubscription,
  hasSubscription,
  allTiers,
  onSelect,
  onUpgrade,
  onDowngrade,
  onCancel,
  addDebugLog,
  displayOnly = false, // Default to false for account page behavior
}) => {
  // Format price for display
  const formatPrice = (amount: number, currency: string) => {
    const price = amount / 100; // Convert from cents
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(price);
  };

  // Determine if this is a coming soon product
  const isComingSoon = tier.metadata?.status === 'coming-soon';

  // Determine the relationship to current plan
  const getCurrentPlanAmount = () => {
    if (!currentSubscription || !hasSubscription) return 0;
    
    // Find the current tier by matching the product ID
    const currentTier = allTiers.find(t => t.id === currentSubscription.stripe_product_id);
    return currentTier ? currentTier.amount : 0;
  };

  const isUpgrade = hasSubscription && tier.amount > getCurrentPlanAmount();
  const isDowngrade = hasSubscription && tier.amount < getCurrentPlanAmount();

  // Debug logging to trace the issue
  // console.log(`[ProductTierCard] ${tier.name}:`, {
  //   hasSubscription,
  //   tierAmount: tier.amount,
  //   currentAmount: getCurrentPlanAmount(),
  //   isUpgrade,
  //   isDowngrade,
  //   isCurrentPlan,
  //   onUpgrade: !!onUpgrade,
  //   onDowngrade: !!onDowngrade,
  //   onSelect: !!onSelect
  // });
  
  addDebugLog && addDebugLog(`ProductTierCard ${tier.name}: hasSubscription=${hasSubscription}, isUpgrade=${isUpgrade}, isCurrentPlan=${isCurrentPlan}, onUpgrade=${!!onUpgrade}`);

  const getActionButton = () => {
    addDebugLog && addDebugLog(`ProductTierCard ${tier.name}: Determining button type - isCurrentPlan=${isCurrentPlan}, hasSubscription=${hasSubscription}, isUpgrade=${isUpgrade}, isDowngrade=${isDowngrade}, displayOnly=${displayOnly}, isComingSoon=${isComingSoon}`);
    
    // On home page (display only mode), show "Get Started" button
    if (displayOnly) {
      return (
        <button className="select-plan-button" onClick={() => {
          console.log(`[ProductTierCard] Get Started button clicked for ${tier.name}`);
          addDebugLog && addDebugLog(`ProductTierCard: Get Started button clicked for ${tier.name}`);
          onSelect();
        }}>
          Get Started
        </button>
      );
    }
    
    // For coming soon products on account page, disable the button
    if (isComingSoon && !displayOnly) {
      return (
        <button className="select-plan-button coming-soon-disabled" disabled>
          Coming Soon
        </button>
      );
    }
    
    if (isCurrentPlan) {
      return (
        <div className="current-plan-actions">
          <button className="select-plan-button current" disabled>
            Current Plan
          </button>
        </div>
      );
    }

    if (!hasSubscription) {
      return (
        <button className="select-plan-button" onClick={() => {
          console.log(`[ProductTierCard] Select plan button clicked for ${tier.name}`);
          addDebugLog && addDebugLog(`ProductTierCard: Select plan button clicked for ${tier.name}`);
          onSelect();
        }}>
          Select
        </button>
      );
    }

    // User has subscription but this is not their current plan
    if (isUpgrade && onUpgrade) {
      return (
        <button className="upgrade-button" onClick={() => {
          console.log(`[ProductTierCard] Upgrade button clicked for ${tier.name}`);
          addDebugLog && addDebugLog(`ProductTierCard: Upgrade button clicked for ${tier.name}`);
          onUpgrade();
        }}>
          Select
        </button>
      );
    }

    if (isDowngrade && onDowngrade) {
      return (
        <button className="downgrade-button" onClick={() => {
          console.log(`[ProductTierCard] Downgrade button clicked for ${tier.name}`);
          addDebugLog && addDebugLog(`ProductTierCard: Downgrade button clicked for ${tier.name}`);
          onDowngrade();
        }}>
          Select
        </button>
      );
    }

    // Fallback for subscription users - same price tier or other edge cases
    return (
      <button className="select-plan-button" onClick={() => {
        console.log(`[ProductTierCard] Change plan button clicked for ${tier.name}`);
        addDebugLog && addDebugLog(`ProductTierCard: Change plan button clicked for ${tier.name}`);
        onSelect();
      }}>
        Select
      </button>
    );
  };

  return (
    <div className={`product-tier-card ${isCurrentPlan ? 'current-plan' : ''} ${isComingSoon ? 'coming-soon' : ''}`}
      data-testid={`plan-card-${tier.id}`}
    >
      {isCurrentPlan && (
        <div className="current-badge">Current Plan</div>
      )}

      <div className="tier-header">
        <h3 className="tier-name">{tier.name}</h3>
        <div className="tier-price">
          <span className="price">{formatPrice(tier.amount, tier.currency)}</span>
          <span className="interval">/{tier.interval}</span>
        </div>
      </div>

      <div className="tier-description">
        <p>{tier.description}</p>
      </div>

      <div className="tier-features">
        <ul>
          {tier && tier.features && Array.isArray(tier.features) ? tier.features.map((feature, index) => (
            <li key={index}>
              <span className="feature-icon">✓</span>
              {feature}
            </li>
          )) : null}
        </ul>
      </div>

      <div className="tier-action">
        {React.cloneElement(getActionButton(), { 'data-testid': `plan-select-${tier.id}` })}
      </div>
    </div>
  );
};

export default ProductTierCard;
